import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService, CurrentFacilityService } from 'src/app/shared/services';
import { FeatureRestrictionsService } from '../../services/features/feature-restrictions.service';
import { PaidToFacilitiesId } from '../../services/firebase/models/organizations';
import { InvalidFieldComponent } from '../field-error/field-error.component';
import { FacilityIdFormGroupType, FacilityIdFormType } from './models/facility-id-form';
import { FacilitiesIdDropdownService } from './services/facilities-id-dropdown.service';
import { formFacilityIdErrors } from './utils/form-errors';

@Component({
    standalone: true,
    selector: 'app-facilities-id-dropdown',
    templateUrl: './facilities-id-dropdown.component.html',
    styleUrls: ['./facilities-id-dropdown.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, InvalidFieldComponent],
})
export class FacilitiesIdDropdownComponent implements OnInit, OnDestroy {
    public facilityIdForm: FacilityIdFormGroupType;
    public options: PaidToFacilitiesId[] = [];
    public showDropdown = false;
    public formFacilityIdErrors = formFacilityIdErrors;
    private unsubscribe$ = new Subject<void>();
    private isPaidToMultipleFacilitiesEnabled: boolean = false;
    private initialForm: Partial<FacilityIdFormType> = {};

    @Input() label = 'Paid to facility';
    @Input()
    set initialFormData(initialForm: Partial<FacilityIdFormType>) {
        this.initialForm = initialForm;

        if (this.initialForm) {
            this.patchForm(initialForm);
        }
    }

    get facilityIdControl(): AbstractControl<number | null> | null {
        return this.facilityIdForm?.get('paidToFacilityId') || null;
    }

    constructor(
        private currentFacilityService: CurrentFacilityService,
        private configService: ConfigService,
        private fb: NonNullableFormBuilder,
        private facilitiesIdDropdownService: FacilitiesIdDropdownService,
        private featureRestrictionsService: FeatureRestrictionsService
    ) {}

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.getFacilityConfig();
        this.setDefaultValueIfNeeded();
        this.showDropdown = this.isNeededToShowDropdown();

        if (this.showDropdown) {
            this.createForm();
            this.saveFacilityId();
        }
    }

    getFacilityConfig(): void {
        this.isPaidToMultipleFacilitiesEnabled = this.configService.isPaidToMultipleFacilitiesEnable();
        this.options = this.configService.getPaidToFacilitiesId() || [];
    }

    createForm(): void {
        this.facilityIdForm = <FacilityIdFormGroupType>this.fb.group({
            paidToFacilityId: [null, [Validators.required]],
        });

        this.patchForm(this.initialForm);
        this.facilitiesIdDropdownService.setForm(this.facilityIdForm);
    }

    isNeededToShowDropdown(): boolean {
        return this.featureRestrictionsService.isEnableToPayMultipleFacilities();
    }

    setDefaultValueIfNeeded(): void {
        if (this.isPaidToMultipleFacilitiesEnabled && this.options.length === 1) {
            this.currentFacilityService.setPaidToFacilityId(this.options[0]?.paidToFacilityId);
        }
    }

    saveFacilityId(): void {
        this.facilityIdControl?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            const facilityId = Number(value);
            this.currentFacilityService.setPaidToFacilityId(facilityId ? facilityId : undefined);
        });
    }

    patchForm(initialForm: Partial<FacilityIdFormType>): void {
        this.facilityIdForm?.patchValue(initialForm);
        this.facilityIdForm?.updateValueAndValidity();
    }
}
